import React from 'react'
import styled from '@emotion/styled'
import { useTheme } from 'emotion-theming'
import { string, bool } from 'prop-types'
import { truncateString } from '@jelly/utils'
import dayjs from 'dayjs'

import { Arrow } from '../svgs/Arrow'
import { Link } from '@jelly/ui'

const TopBanner = styled.div`
	align-items: center;
	display: flex;
	background: ${({ theme }) => theme.arrowActionCard.topBannerBackground};
	justify-content: space-between;
	min-height: 47px;
	padding: 0 1.125rem;
	transition: all 0.4s ease;
	width: 100%;
`
const CardWrapper = styled(Link)`
	background: ${({ theme }) => theme.colors.white};
	display: flex;
	flex-direction: column;
	height: auto;
	min-height: 180px;
	position: relative;
	width: auto;

	&:hover {
		${TopBanner} {
			background: ${({ theme }) => theme.arrowActionCard.topBannerHover};
		}
	}
`
const Date = styled.div`
	color: ${({ theme }) => theme.colors.white};
	font-size: 16px;
	letter-spacing: 2px;
`
const ArrowWrapper = styled.div`
	transform: rotate(270deg);
`
const CardBody = styled.div`
	color: ${({ theme }) => theme.colors.general};
	padding: 12px 1.125rem;
	height: 100%;
	min-height: 133px;
	border: 2px solid ${({ theme }) => theme.colors.gray[400]};
	border-top: none;
`
const SponsoredBadge = styled.div`
	color: ${({ theme }) => theme.colors.gray[500]};
	font-size: 12px;
	padding-bottom: 8px;
`
const Title = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	line-height: 24px;
	letter-spacing: -0.2px;
	margin-bottom: 0.5rem;

	font-size: 1.25rem;
	line-height: 2rem;
`
const Description = styled.div`
	font-size: 12px;
	line-height: 20px;
	letter-spacing: 0.5px;
`

ArrowActionCard.propTypes = {
	/**
	 * Date corresponding to the content
	 */
	date: string.isRequired,
	/**
	 * Quick description of the content the card is linked to
	 */
	description: string.isRequired,
	/**
	 * Url path for the card destination
	 */
	canonical: string.isRequired,
	/**
	 * Title of the content
	 */
	title: string.isRequired,
	/**
	 * Adds Sponsored title
	 */
	sponsored: bool,
}

/**
 * Arrow Action Card, provides an action destination to any given url
 * using the Link component adding required attr
 * Displays a date, title and description with a primary arrow and hover state
 */
export function ArrowActionCard({ canonical, date, description, isUpdated, sponsored, title }) {
	const {
		arrowActionCard: { arrowColor },
	} = useTheme()
	const truncatedTitle = truncateString(title, 52)
	const truncatedDescription = truncateString(description, 67)
	return (
		<CardWrapper alt={title} href={canonical}>
			<TopBanner>
				<Date>
					{isUpdated && 'Updated'} {dayjs(date).format('MM.DD.YY')}
				</Date>
				<ArrowWrapper>
					<Arrow height={21} color={arrowColor} width={19} />
				</ArrowWrapper>
			</TopBanner>
			<CardBody>
				{sponsored && <SponsoredBadge>Sponsored Content</SponsoredBadge>}
				<Title>{truncatedTitle}</Title>
				<Description>{truncatedDescription}</Description>
			</CardBody>
		</CardWrapper>
	)
}
